/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { defaultShownMenuEditorTabs } from "components/MenuEditorContent"
import { MenuEditorTabKey, menuEditorTabKeys } from "constants/menu"
import useDelvPlatformFeatureFlags from "hooks/useDelvPlatformFeatureFlags"
import useInventoryContext from "hooks/useInventoryContext"
import { DELV_PLATFORM_FEATURE_KEYS } from "types/delv-platform-api"
import { actionCreator } from "store/store"

export const useShownTabs = () => {
  const { enableInventory, isInventory } = useInventoryContext()

  const {
    fetchStarted,
    isLoading,
    error,
    featureFlags,
  } = useDelvPlatformFeatureFlags([DELV_PLATFORM_FEATURE_KEYS.BULK_UPLOAD])
  const dispatch = useDispatch()
  const [shownTabs, setShownTabs] = useState<MenuEditorTabKey[]>([])

  useEffect(() => {
    let temp = defaultShownMenuEditorTabs

    /**
     * if enableInventory true and in inventory page -> remove menu overview tab
     */
    if (enableInventory && isInventory)
      temp = temp.filter(item => item !== menuEditorTabKeys.menuOverview)

    /**
     * if enableInventory true and in not in inventory page page -> remove item tab
     */
    if (enableInventory && !isInventory)
      temp = temp.filter(item => item !== menuEditorTabKeys.items)

    if (fetchStarted && !isLoading) {
      /**
       * handle bulk upload inventory
       */
      if (
        !error &&
        featureFlags?.bulkUpload?.enableUpdateInventory &&
        isInventory
      ) {
        temp.push(menuEditorTabKeys.bulkUploadInventory)
      }

      /**
       * handle bulk upload menu
       */
      if (
        !error &&
        featureFlags?.bulkUpload?.enableUpdateItem &&
        !isInventory
      ) {
        temp.push(menuEditorTabKeys.bulkUploadMenu)
      }

      setShownTabs(temp)
    }
  }, [
    enableInventory,
    fetchStarted,
    isLoading,
    error,
    featureFlags,
    isInventory,
  ])

  // clean up only once
  // don't clear featureFlags in useDelvPlatformFeatureFlags, does not make sense to do it there, that hook should be used to get featureFlags only
  // user can back to store listing page to select another store -> need to clean the current featureFlag, otherwise un-whitelisted store may still can see BulkUpload tab
  useEffect(() => {
    return () => {
      dispatch(actionCreator.global.clearDelvPlatformFeatureFlags())
    }
  }, [dispatch])

  return shownTabs
}
